import WorldIcon from '@atlaskit/icon/glyph/world';
import Select, { OptionType } from '@atlaskit/select';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { selectCurrentLanguage, selectSupportedLanguages, switchLanguage } from 'services/localization';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks';
import { token } from '@atlaskit/tokens';
import { Box, xcss } from '@atlaskit/primitives';

const languageSelectStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.025',
});

const LanguageSelect: FC = () => {
  const dispatch = useAppDispatch();
  const selectLanguageHandler = useCallback(
    (option: OptionType | null) => {
      if (option) {
        dispatch(switchLanguage(option.value as string));
      }
    },
    [dispatch],
  );

  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const supportedLanguages = useAppSelector(selectSupportedLanguages);

  const languageOptions = useMemo(
    () =>
      Object.keys(supportedLanguages).map((key) => ({
        value: key,
        label: supportedLanguages[key],
      })),
    [supportedLanguages],
  );
  const defaultLanguage = useMemo(
    () => ({
      value: currentLanguage,
      label: supportedLanguages[currentLanguage],
    }),
    [currentLanguage, supportedLanguages],
  );

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  return (
    <Box xcss={languageSelectStyles}>
      <WorldIcon
        label={'Language Select'}
        primaryColor={token('color.icon.subtle')}
        secondaryColor={token('color.icon.subtle')}
      />
      <Select
        appearance={'subtle'}
        classNamePrefix={'languageSelectWrapper'}
        value={defaultLanguage}
        options={languageOptions}
        onChange={selectLanguageHandler}
        menuPlacement={'top'}
        blurInputOnSelect={true}
      />
    </Box>
  );
};

export default LanguageSelect;
